<template>
  <div class="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md">
    <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Top Account Toevoegen</h3>

    <form @submit.prevent="handleSubmit" class="space-y-4">
      <div>
        <label
          for="display_name"
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Weergavenaam (bedrijfsnaam)
        </label>
        <input
          id="display_name"
          v-model="form.display_name"
          type="text"
          placeholder="Bedrijfsnaam voor in de lijst"
          class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          required
        />
      </div>

      <div>
        <label
          for="simplicate_urls"
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Simplicate URL's of Organization ID's
          <span class="text-gray-500 font-normal">(één per regel)</span>
        </label>
        <textarea
          id="simplicate_urls"
          v-model="form.simplicate_urls"
          rows="4"
          placeholder="https://frisseblikken.simplicate.nl/crm/organizations/organization:xxx/overview
https://frisseblikken.simplicate.nl/crm/organizations/organization:yyy/overview"
          class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
          :class="{ 'border-red-500 dark:border-red-500': urlError }"
        />
        <p v-if="urlError" class="mt-1 text-sm text-red-600 dark:text-red-400">
          {{ urlError }}
        </p>
        <p v-else-if="validCount > 0" class="mt-1 text-sm text-green-600 dark:text-green-400">
          {{ validCount }} geldige organization ID{{ validCount > 1 ? "'s" : '' }} gevonden
        </p>
      </div>

      <div class="flex justify-end gap-2">
        <button
          type="submit"
          :disabled="!isValid || isSubmitting"
          class="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
        >
          {{
            isSubmitting ? 'Toevoegen...' : `Toevoegen${validCount > 1 ? ` (${validCount})` : ''}`
          }}
        </button>
      </div>

      <p v-if="submitError" class="text-red-600 dark:text-red-400 text-sm">
        {{ submitError }}
      </p>

      <div v-if="submitResults.length > 0" class="space-y-2">
        <div
          v-for="(result, idx) in submitResults"
          :key="idx"
          :class="[
            'text-sm p-2 rounded',
            result.success
              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          ]"
        >
          {{ result.message }}
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { extractOrganizationId } from '@/composables/data/useTop20Accounts'

const emit = defineEmits(['submit'])

const form = ref({
  simplicate_urls: '',
  display_name: ''
})

const isSubmitting = ref(false)
const submitError = ref('')
const submitResults = ref([])

// Parse textarea into lines and extract IDs
const parsedLines = computed(() => {
  if (!form.value.simplicate_urls) return []

  return form.value.simplicate_urls
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map(line => ({
      original: line,
      organizationId: extractOrganizationId(line)
    }))
})

const validEntries = computed(() => parsedLines.value.filter(entry => entry.organizationId))
const invalidEntries = computed(() => parsedLines.value.filter(entry => !entry.organizationId))
const validCount = computed(() => validEntries.value.length)

const urlError = computed(() => {
  if (!form.value.simplicate_urls) return ''
  if (parsedLines.value.length === 0) return ''
  if (invalidEntries.value.length > 0) {
    return `${invalidEntries.value.length} regel(s) bevatten geen geldig organization ID`
  }
  return ''
})

const isValid = computed(() => {
  return (
    validEntries.value.length > 0 &&
    invalidEntries.value.length === 0 &&
    form.value.display_name.trim()
  )
})

async function handleSubmit() {
  if (!isValid.value) return

  isSubmitting.value = true
  submitError.value = ''
  submitResults.value = []

  const displayName = form.value.display_name.trim()
  const entries = validEntries.value

  try {
    // Submit all entries via bulk endpoint
    await emit('submit', {
      display_name: displayName,
      organization_ids: entries.map(e => e.organizationId)
    })

    // Reset form on success
    form.value = { simplicate_urls: '', display_name: '' }
  } catch (e) {
    submitError.value = e.response?.data?.error || 'Fout bij toevoegen'
  } finally {
    isSubmitting.value = false
  }
}
</script>
